import {
  AppCompliance,
  AppConfigUpdateModel,
  AppsApi,
  IndexPageBase,
  Language,
  RestApiResultWebsiteImage,
  WebsiteApi,
  WebsiteImage,
  WebsiteTestimonialBase,
} from '@flipdish/api-client-typescript';
import {
  AppExternalServices,
  AppsApi as AppsPrivateApi,
  AppSocialMediaLinks,
} from '@flipdish/api-client-typescript/private/api';
import axios from 'axios';

import { getApiEndPoint } from '../../helpers/apibase';
import { CredentialsAuth } from '../../helpers/utilities';
import { mapServerError } from '../../services/utils/serverErrorMapper';

const baseUrl = getApiEndPoint();
const extraApi = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
});

const appsApi = new AppsApi(getApiEndPoint());
appsApi.setDefaultAuthentication(new CredentialsAuth());

const websiteApi = new WebsiteApi(getApiEndPoint());
websiteApi.setDefaultAuthentication(new CredentialsAuth());

const appsPrivateApi = new AppsPrivateApi(getApiEndPoint());
appsPrivateApi.setDefaultAuthentication(new CredentialsAuth());

const setAppConfig = async (appId: string, appConfigUpdate: AppConfigUpdateModel) => {
  try {
    const incomingMessage = await appsApi.setAppConfig(appId, appConfigUpdate);

    return incomingMessage.body.Data;
  } catch (e) {
    throw mapServerError(e);
  }
};

const uploadAppConfigLogo = async (appId: string, data: FormData) => {
  try {
    await extraApi.post(`/api/v1.0/apps/${appId}/logo`, data);
  } catch (e) {
    throw mapServerError(e);
  }
};

const setAppConfigLanguages = async (appId: string, languages: Language[]) => {
  try {
    await appsApi.setAppLanguages(appId, languages);
  } catch (e) {
    throw mapServerError(e);
  }
};

const getWebsiteSettings = async (appId: string) => {
  try {
    const incomingMessage = await websiteApi.getIndexConfiguration(appId);

    return incomingMessage.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const setWebsiteSettings = async (appId: string, indexPage: IndexPageBase) => {
  try {
    const incomingMessage = await websiteApi.setIndexConfiguration(appId, indexPage);

    return incomingMessage.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const addTestimonial = async (appId: string, testimonial: WebsiteTestimonialBase) => {
  try {
    const incomingMessage = await websiteApi.addTestimonial(appId, testimonial);

    return incomingMessage.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const updateTestimonial = async (
  appId: string,
  testimonialId: number,
  testimonial: WebsiteTestimonialBase
) => {
  try {
    const incomingMessage = await websiteApi.editTestimonial(appId, testimonialId, testimonial);

    return incomingMessage.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const deleteTestimonial = async (appId: string, testimonialId: number) => {
  try {
    await websiteApi.deleteTestimonial(appId, testimonialId);
  } catch (e) {
    throw mapServerError(e);
  }
};

export enum ImageLocationEnum {
  IndexHeader = 4,
  IndexGallery = 7,
  IndexOpeningHoursHeader = 8,
  IndexTestimonialsHeader = 9,
  GalleryHeader = 4,
  IndexAboutSectionLeft = 5,
  IndexAboutSectionRight = 6,
}

const uploadImage = async (appId: string, imageLocation: WebsiteImage.ImageLocationEnum, data) => {
  try {
    const incomingMessage = await extraApi.post<RestApiResultWebsiteImage>(
      `/api/v1.0/${appId}/website/image/${imageLocation}`,
      data
    );

    return incomingMessage.data.Data;
  } catch (e) {
    throw mapServerError(e);
  }
};

const deleteImage = async (appId: string, imageId: number) => {
  try {
    await websiteApi.deleteWebsiteImage(appId, imageId);
  } catch (e) {
    throw mapServerError(e);
  }
};

//#region SetupDomain
const setAppHostName = async (appId: string, hostname: string, isEmbed?: boolean) => {
  try {
    await appsApi.setAppHostname(appId, hostname, isEmbed);
  } catch (e) {
    throw mapServerError(e);
  }
};

const checkDNS = async (appId: string) => {
  try {
    await websiteApi.websiteCheckNow(appId);
  } catch (e) {
    throw mapServerError(e);
  }
};

const getDNSStatus = async (appId: string) => {
  try {
    const incomingMessage = await appsApi.getAppHostnameStatus(appId);

    return incomingMessage.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const getPanaceaUrl = async (appId: string) => {
  try {
    const incomingMessage = await appsApi.getPanaceaVanityUrl(appId);

    return incomingMessage.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const setPanaceaUrl = async (appId: string, vanityUrl: string) => {
  try {
    await appsApi.setPanaceaVanityUrl(appId, vanityUrl);
  } catch (e) {
    throw mapServerError(e);
  }
};

const getPanaceaUrlStatus = async (vanityUrl: string, appId: string) => {
  try {
    const incomingMessage = await appsApi.isPanaceaVanityUrlAvailable(vanityUrl, appId);
    return incomingMessage.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

export const getAppEmbeddedStatus = async (appId: string) => {
  try {
    const response = await appsPrivateApi.isAppEmbedded(appId);
    return response.body.Data.IsEmbedded;
  } catch (e) {
    throw mapServerError(e);
  }
};
//#endregion SetupDomain

const getTrackingCodes = async (appId: string) => {
  try {
    const response = await appsPrivateApi.getExternalServices(appId);
    return response.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const setTrackingCodes = async (appId: string, appExternalServices: AppExternalServices) => {
  try {
    await appsPrivateApi.setExternalTrackingCodes(appId, appExternalServices);
  } catch (e) {
    throw mapServerError(e);
  }
};

const getsetSMSRestaurantName = async (appId: string) => {
  try {
    const response = await appsPrivateApi.getSmsRestaurantName(appId);
    return response.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const setSMSRestaurantName = async (appId: string, smsRestaurantName: string) => {
  try {
    await appsPrivateApi.setSmsName(appId, smsRestaurantName);
  } catch (e) {
    throw mapServerError(e);
  }
};

const getSocialMediaLinks = async (appId: string) => {
  try {
    const response = await appsPrivateApi.getSocialMediaLinks(appId);
    return response.body;
  } catch (e) {
    throw mapServerError(e);
  }
};

const setSocialMediaLinks = async (appId: string, socialMediaLinks: AppSocialMediaLinks) => {
  try {
    await appsPrivateApi.setSocialMediaLinks(appId, socialMediaLinks);
  } catch (e) {
    throw mapServerError(e);
  }
};

const getCompliance = async (appId: string) => {
  try {
    const incomingMessage = await appsApi.getCompliance(appId);
    return incomingMessage.body.Data;
  } catch (e) {
    throw mapServerError(e);
  }
};

const setCompliance = async (appId: string, complianceType: AppCompliance.ComplianceTypeEnum) => {
  try {
    // Generated typescript code has bug with wrong typing for enum.
    const incomingMessage = await appsApi.setCompliance(
      appId,
      (complianceType as unknown) as 'Default' | 'GdprCompliance'
    );
    return incomingMessage.body.Data;
  } catch (e) {
    throw mapServerError(e);
  }
};

const toggleNextGenWeb = async (
  appId: string,
  hostname: string,
  vanityUrl: string,
  isNextGenWeb: boolean
) => {
  try {
    return await appsApi.toggleNextGenWeb(appId, hostname, vanityUrl, isNextGenWeb);
  } catch (e) {
    throw mapServerError(e);
  }
};

const WebsiteAndAppService = {
  setAppConfig,
  uploadAppConfigLogo,
  setAppConfigLanguages,
  getWebsiteSettings,
  setWebsiteSettings,
  addTestimonial,
  updateTestimonial,
  uploadImage,
  deleteImage,
  deleteTestimonial,
  setAppHostName,
  getDNSStatus,
  checkDNS,
  getPanaceaUrl,
  setPanaceaUrl,
  getPanaceaUrlStatus,
  getTrackingCodes,
  setTrackingCodes,
  getAppEmbeddedStatus,
  setSMSRestaurantName,
  getsetSMSRestaurantName,
  getSocialMediaLinks,
  setSocialMediaLinks,
  getCompliance,
  setCompliance,
  toggleNextGenWeb,
};

export default WebsiteAndAppService;
